/**
 * TanStack Query hooks for every store-settings domain.
 *
 * One read hook (`use<Domain>Settings`) and one write hook
 * (`useUpdate<Domain>Settings`) per domain, plus the order-lifecycle
 * sub-resource. Provider CRUD and connection-test actions are owned by their
 * reusable UI (`ProviderManager`, `TestConnectionButton`) which compose the
 * generic mutation hooks directly.
 *
 * Every hook delegates HTTP to the typed API clients in `../api`, so screens
 * never touch the transport layer.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import { getCatalogSettings, updateCatalogSettings } from '../api/catalogSettingsApi';
import { getCheckoutSettings, updateCheckoutSettings } from '../api/checkoutSettingsApi';
import { getContentSettings, updateContentSettings } from '../api/contentSettingsApi';
import { getCustomerSettings, updateCustomerSettings } from '../api/customerSettingsApi';
import { getDeliverySettings, updateDeliverySettings } from '../api/deliverySettingsApi';
import { getEngagementSettings, updateEngagementSettings } from '../api/engagementSettingsApi';
import {
  getNotificationSettings,
  updateNotificationSettings,
} from '../api/notificationSettingsApi';
import {
  getOrderLifecycle,
  getOrderSettings,
  updateOrderLifecycle,
  updateOrderSettings,
} from '../api/orderSettingsApi';
import { getPaymentSettings, updatePaymentSettings } from '../api/paymentSettingsApi';
import { getPricingSettings, updatePricingSettings } from '../api/pricingSettingsApi';
import { getMaxSettings, updateMaxSettings } from '../api/maxSettingsApi';
import { getGeneralSettings, updateGeneralSettings } from '../api/storeSettingsApi';
import { getTelegramSettings, updateTelegramSettings } from '../api/telegramSettingsApi';
import {
  DEFAULT_BOT_MESSAGE_LOCALE,
  createFaqEntry,
  deleteFaqEntry,
  listBotMessages,
  listFaqEntries,
  resetBotMessage,
  updateFaqEntry,
  upsertBotMessage,
} from '../api/telegramBotMessagesApi';
import { getTenantSettings, updateTenantSettings } from '../api/tenantSettingsApi';
import {
  getStoreProfileSettings,
  updateStoreProfileSettings,
} from '../api/storeProfileSettingsApi';
import { getAddressSettings, updateAddressSettings } from '../api/addressSettingsApi';
import { getInventorySettings, updateInventorySettings } from '../api/inventorySettingsApi';
import { getMediaSettings, updateMediaSettings } from '../api/mediaSettingsApi';
import { getAiAgentSettings, updateAiAgentSettings } from '../api/aiAgentSettingsApi';
import { getBasketSettings, updateBasketSettings } from '../api/basketSettingsApi';
import { uploadTenantLogo } from '../api/tenantLogoApi';
import type { UploadedAsset, UploadTenantLogoParams } from '../api/tenantLogoApi';
import * as storeServiceApi from '../api/storeServiceApi';
import type {
    BotMessage,
    CreateFaqRequest,
    CreateStoreLocationRequest,
    CreateStoreRegionRequest,
    FaqEntry,
    StoreLocationSearchRequest,
    StoreRegionSearchRequest,
    UpdateFaqRequest,
    UpdateStoreLocationRequest,
    UpdateStoreRegionRequest,
    UpsertBotMessageRequest,
} from '../model/settingsTypes';

import { SETTINGS_ROOT_KEY, settingsKeys } from './queryKeys';
import { useSettingsMutation, useSettingsQuery } from './useSettingsResource';

// ── 1. General ────────────────────────────────────────────────────────────────
export const useGeneralSettings = () => useSettingsQuery(settingsKeys.general, getGeneralSettings);
export const useUpdateGeneralSettings = () =>
  useSettingsMutation(settingsKeys.general, updateGeneralSettings);

// ── 2. Delivery ─────────────────────────────────────────────────────────────
export const useDeliverySettings = () =>
  useSettingsQuery(settingsKeys.delivery, getDeliverySettings);
export const useUpdateDeliverySettings = () =>
  useSettingsMutation(settingsKeys.delivery, updateDeliverySettings);

// ── 3. Payments ───────────────────────────────────────────────────────────────
export const usePaymentSettings = () => useSettingsQuery(settingsKeys.payments, getPaymentSettings);
export const useUpdatePaymentSettings = () =>
  useSettingsMutation(settingsKeys.payments, updatePaymentSettings);

// ── 4. Checkout ───────────────────────────────────────────────────────────────
export const useCheckoutSettings = () =>
  useSettingsQuery(settingsKeys.checkout, getCheckoutSettings);
export const useUpdateCheckoutSettings = () =>
  useSettingsMutation(settingsKeys.checkout, updateCheckoutSettings);

// ── 5. Orders + lifecycle ─────────────────────────────────────────────────────
export const useOrderSettings = () => useSettingsQuery(settingsKeys.orders, getOrderSettings);
export const useUpdateOrderSettings = () =>
  useSettingsMutation(settingsKeys.orders, updateOrderSettings);
export const useOrderLifecycle = () =>
  useSettingsQuery(settingsKeys.ordersLifecycle, getOrderLifecycle);
export const useUpdateOrderLifecycle = () =>
  useSettingsMutation(settingsKeys.ordersLifecycle, updateOrderLifecycle);

// ── 6. Pricing ────────────────────────────────────────────────────────────────
export const usePricingSettings = () => useSettingsQuery(settingsKeys.pricing, getPricingSettings);
export const useUpdatePricingSettings = () =>
  useSettingsMutation(settingsKeys.pricing, updatePricingSettings);

// ── 7. Catalog ────────────────────────────────────────────────────────────────
export const useCatalogSettings = () => useSettingsQuery(settingsKeys.catalog, getCatalogSettings);
export const useUpdateCatalogSettings = () =>
  useSettingsMutation(settingsKeys.catalog, updateCatalogSettings);

// ── 8. Customers ──────────────────────────────────────────────────────────────
export const useCustomerSettings = () =>
  useSettingsQuery(settingsKeys.customers, getCustomerSettings);
export const useUpdateCustomerSettings = () =>
  useSettingsMutation(settingsKeys.customers, updateCustomerSettings);

// ── 9. Notifications ──────────────────────────────────────────────────────────
export const useNotificationSettings = () =>
  useSettingsQuery(settingsKeys.notifications, getNotificationSettings);
export const useUpdateNotificationSettings = () =>
  useSettingsMutation(settingsKeys.notifications, updateNotificationSettings);

// ── 10. Content ───────────────────────────────────────────────────────────────
export const useContentSettings = () => useSettingsQuery(settingsKeys.content, getContentSettings);
export const useUpdateContentSettings = () =>
  useSettingsMutation(settingsKeys.content, updateContentSettings);

// ── 11. Engagement ────────────────────────────────────────────────────────────
export const useEngagementSettings = () =>
  useSettingsQuery(settingsKeys.engagement, getEngagementSettings);
export const useUpdateEngagementSettings = () =>
  useSettingsMutation(settingsKeys.engagement, updateEngagementSettings);

// ── 12. Telegram ──────────────────────────────────────────────────────────────
export const useTelegramSettings = () =>
  useSettingsQuery(settingsKeys.telegram, getTelegramSettings);
export const useUpdateTelegramSettings = () =>
  useSettingsMutation(settingsKeys.telegram, updateTelegramSettings);

// ── 12a. MAX ─────────────────────────────────────────────────────────────────
export const useMaxSettings = () => useSettingsQuery(settingsKeys.max, getMaxSettings);
export const useUpdateMaxSettings = () => useSettingsMutation(settingsKeys.max, updateMaxSettings);

// ── 12b. Telegram bot messages + FAQ (epic-002) ─────────────────────────────
//
// Not a single `VersionedSettings` resource — a *catalog* of independently
// versioned items. Every write invalidates the owning list so the UI always
// reflects the server's per-item version (needed for the next optimistic-lock
// save), mirroring the `ProviderManager` list-CRUD pattern.
export const useBotMessages = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) =>
  useSettingsQuery(settingsKeys.telegramMessages(locale), () => listBotMessages(locale));

export const useUpsertBotMessage = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) => {
  const queryClient = useQueryClient();
  return useMutation<BotMessage, NormalizedApiError, { contentKey: string; body: UpsertBotMessageRequest }>({
    mutationFn: ({ contentKey, body }) => upsertBotMessage(contentKey, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.telegramMessages(locale) }),
  });
};

export const useResetBotMessage = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) => {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: (contentKey) => resetBotMessage(contentKey, locale),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.telegramMessages(locale) }),
  });
};

export const useFaqEntries = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) =>
  useSettingsQuery(settingsKeys.telegramMessagesFaq(locale), () => listFaqEntries(locale));

export const useCreateFaqEntry = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) => {
  const queryClient = useQueryClient();
  return useMutation<FaqEntry, NormalizedApiError, CreateFaqRequest>({
    mutationFn: createFaqEntry,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.telegramMessagesFaq(locale) }),
  });
};

export const useUpdateFaqEntry = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) => {
  const queryClient = useQueryClient();
  return useMutation<FaqEntry, NormalizedApiError, { contentKey: string; body: UpdateFaqRequest }>({
    mutationFn: ({ contentKey, body }) => updateFaqEntry(contentKey, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.telegramMessagesFaq(locale) }),
  });
};

export const useDeleteFaqEntry = (locale: string = DEFAULT_BOT_MESSAGE_LOCALE) => {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: (contentKey) => deleteFaqEntry(contentKey, locale),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.telegramMessagesFaq(locale) }),
  });
};

// ── 13. Tenant ────────────────────────────────────────────────────────────────
export const useTenantSettings = () => useSettingsQuery(settingsKeys.tenant, getTenantSettings);
export const useUpdateTenantSettings = () =>
  useSettingsMutation(settingsKeys.tenant, updateTenantSettings);
export const useUploadTenantLogo = () =>
  useMutation<UploadedAsset, NormalizedApiError, UploadTenantLogoParams>({
    mutationFn: uploadTenantLogo,
  });

// ── 14. Store profile ───────────────────────────────────────────────────────
export const useStoreProfileSettings = () =>
  useSettingsQuery(settingsKeys.store, getStoreProfileSettings);
export const useUpdateStoreProfileSettings = () =>
  useSettingsMutation(settingsKeys.store, updateStoreProfileSettings);

export const useStoreServiceSettings = () =>
  useSettingsQuery(settingsKeys.storeServiceSettings, storeServiceApi.getStoreServiceSettings);
export const useUpdateStoreServiceSettings = () =>
  useSettingsMutation(settingsKeys.storeServiceSettings, storeServiceApi.updateStoreServiceSettings);

export const useStoreLocations = (params: StoreLocationSearchRequest) =>
  useQuery({
    queryKey: settingsKeys.storeLocations(params),
    queryFn: () => storeServiceApi.searchStoreLocations(params),
    placeholderData: keepPreviousData,
  });

export const useStoreRegions = (params: StoreRegionSearchRequest) =>
  useQuery({
    queryKey: settingsKeys.storeRegions(params),
    queryFn: () => storeServiceApi.searchStoreRegions(params),
    placeholderData: keepPreviousData,
  });

export const useStoreLocationsByRegion = (regionId: string | null) =>
  useQuery({
    queryKey: [...settingsKeys.storeAssignments, 'region', regionId] as const,
    queryFn: () => storeServiceApi.getStoreLocationsByRegion(regionId ?? ''),
    enabled: Boolean(regionId),
  });

export const useStoreRegionsByLocation = (locationId: string | null) =>
  useQuery({
    queryKey: [...settingsKeys.storeAssignments, 'location', locationId] as const,
    queryFn: () => storeServiceApi.getStoreRegionsByLocation(locationId ?? ''),
    enabled: Boolean(locationId),
  });

function useInvalidateStoreDomain() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: [SETTINGS_ROOT_KEY, 'store'] }),
      qc.invalidateQueries({ queryKey: settingsKeys.storeServiceSettings }),
    ]);
}

export const useCreateStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, CreateStoreLocationRequest>({
    mutationFn: storeServiceApi.createStoreLocation,
    onSuccess: () => void invalidate(),
  });
};

export const useUpdateStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; body: UpdateStoreLocationRequest }>({
    mutationFn: ({ id, body }) => storeServiceApi.updateStoreLocation(id, body),
    onSuccess: () => void invalidate(),
  });
};

export const useDeleteStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: storeServiceApi.deleteStoreLocation,
    onSuccess: () => void invalidate(),
  });
};

export const useToggleStoreLocation = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      active ? storeServiceApi.deactivateStoreLocation(id) : storeServiceApi.activateStoreLocation(id),
    onSuccess: () => void invalidate(),
  });
};

export const useCreateStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, CreateStoreRegionRequest>({
    mutationFn: storeServiceApi.createStoreRegion,
    onSuccess: () => void invalidate(),
  });
};

export const useUpdateStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; body: UpdateStoreRegionRequest }>({
    mutationFn: ({ id, body }) => storeServiceApi.updateStoreRegion(id, body),
    onSuccess: () => void invalidate(),
  });
};

export const useDeleteStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: storeServiceApi.deleteStoreRegion,
    onSuccess: () => void invalidate(),
  });
};

export const useToggleStoreRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      active ? storeServiceApi.deactivateStoreRegion(id) : storeServiceApi.activateStoreRegion(id),
    onSuccess: () => void invalidate(),
  });
};

export const useAssignStoreLocationToRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<unknown, NormalizedApiError, { regionId: string; locationId: string }>({
    mutationFn: ({ regionId, locationId }) =>
      storeServiceApi.assignStoreLocationToRegion(regionId, locationId),
    onSuccess: () => void invalidate(),
  });
};

export const useRemoveStoreLocationFromRegion = () => {
  const invalidate = useInvalidateStoreDomain();
  return useMutation<void, NormalizedApiError, { regionId: string; locationId: string }>({
    mutationFn: ({ regionId, locationId }) =>
      storeServiceApi.removeStoreLocationFromRegion(regionId, locationId),
    onSuccess: () => void invalidate(),
  });
};

// ── 15. Address ───────────────────────────────────────────────────────────────
export const useAddressSettings = () => useSettingsQuery(settingsKeys.address, getAddressSettings);
export const useUpdateAddressSettings = () =>
  useSettingsMutation(settingsKeys.address, updateAddressSettings);

// ── 16. Inventory ─────────────────────────────────────────────────────────────
export const useInventorySettings = () =>
  useSettingsQuery(settingsKeys.inventory, getInventorySettings);
export const useUpdateInventorySettings = () =>
  useSettingsMutation(settingsKeys.inventory, updateInventorySettings);

// ── 17. Media ─────────────────────────────────────────────────────────────────
export const useMediaSettings = () => useSettingsQuery(settingsKeys.media, getMediaSettings);
export const useUpdateMediaSettings = () =>
  useSettingsMutation(settingsKeys.media, updateMediaSettings);

// ── 18. AI agents ─────────────────────────────────────────────────────────────
export const useAiAgentSettings = () => useSettingsQuery(settingsKeys.aiAgents, getAiAgentSettings);
export const useUpdateAiAgentSettings = () =>
  useSettingsMutation(settingsKeys.aiAgents, updateAiAgentSettings);

// ── 19. Basket ────────────────────────────────────────────────────────────────
export const useBasketSettings = () => useSettingsQuery(settingsKeys.basket, getBasketSettings);
export const useUpdateBasketSettings = () =>
  useSettingsMutation(settingsKeys.basket, updateBasketSettings);

export { settingsKeys, SETTINGS_ROOT_KEY } from './queryKeys';
export {
  useInvalidatingMutation,
  useSettingsMutation,
  useSettingsQuery,
} from './useSettingsResource';
